import React from 'react';
import { motion, Variants } from 'framer-motion';
import { Box, BoxProps } from '@mui/material';

type Direction = 'up' | 'down' | 'left' | 'right' | 'none' | 'scale';

interface RevealProps extends Omit<BoxProps, 'component'> {
  delay?: number;
  duration?: number;
  direction?: Direction;
  once?: boolean;
  amount?: number;
  /** stagger children that are themselves <RevealItem> */
  stagger?: number;
}

const offsets: Record<Direction, { x?: number; y?: number; scale?: number }> = {
  up: { y: 32 },
  down: { y: -32 },
  left: { x: 32 },
  right: { x: -32 },
  none: {},
  scale: { scale: 0.92 },
};

/** Scroll-triggered entrance. Wrap any block; it fades/slides in when visible. */
export const Reveal: React.FC<RevealProps> = ({
  children, delay = 0, duration = 0.7, direction = 'up', once = true, amount = 0.25, stagger, sx, ...rest
}) => {
  const variants: Variants = {
    hidden: { opacity: 0, filter: 'blur(6px)', ...offsets[direction] },
    show: {
      opacity: 1, x: 0, y: 0, scale: 1, filter: 'blur(0px)',
      transition: { duration, delay, ease: [0.2, 0.8, 0.2, 1], staggerChildren: stagger, delayChildren: stagger ? delay : undefined },
    },
  };
  return (
    <Box
      component={motion.div}
      variants={variants}
      initial="hidden"
      whileInView="show"
      viewport={{ once, amount }}
      sx={sx}
      {...(rest as any)}
    >
      {children}
    </Box>
  );
};

/** Child of a staggered <Reveal stagger={0.1}> */
export const RevealItem: React.FC<BoxProps & { direction?: Direction }> = ({ children, direction = 'up', sx, ...rest }) => (
  <Box
    component={motion.div}
    variants={{
      hidden: { opacity: 0, filter: 'blur(4px)', ...offsets[direction] },
      show: { opacity: 1, x: 0, y: 0, scale: 1, filter: 'blur(0px)', transition: { duration: 0.6, ease: [0.2, 0.8, 0.2, 1] } },
    }}
    sx={sx}
    {...(rest as any)}
  >
    {children}
  </Box>
);

export default Reveal;
